<template>
  <teleport to="body">
    <div 
      v-if="visible" 
      class="gp-popover" 
      :style="positionStyle"
      @mousedown.stop
      ref="popover"
    >
      <!-- Header -->
      <div class="gp-header">
        <div class="gp-title-section">
          <h4 class="gp-title">{{ groupName }}</h4>
          <span class="gp-indicator-count">{{ items.length }} indicators</span>
        </div>
        <button class="gp-close-btn" @click="$emit('close')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>

      <!-- Rename Section -->
      <div class="gp-section">
        <label class="gp-label">Group Name</label>
        <div class="gp-rename-form">
          <input 
            v-model="editName" 
            class="gp-input"
            type="text"
            placeholder="Enter group name"
            @keyup.enter="handleRename"
            ref="nameInput"
          />
          <button 
            class="gp-btn gp-btn-primary"
            @click="handleRename"
            :disabled="!editName.trim() || editName.trim() === groupName"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Rename
          </button>
        </div>
      </div>

      <!-- Indicators List -->
      <div class="gp-section">
        <div class="gp-section-header">
          <label class="gp-label">Indicators</label>
          <div class="gp-visibility-toggle">
            <button 
              class="gp-visibility-btn"
              @click="$emit('toggle-visibility')"
              :class="{ active: !allVisible }"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
              {{ allVisible ? 'Hide All' : 'Show All' }}
            </button>
          </div>
        </div>
        
        <div v-if="items.length === 0" class="gp-empty-indicators">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          <p>No indicators in this group</p>
        </div>
        
        <div v-else class="gp-indicators-list">
          <div 
            v-for="indicator in items" 
            :key="indicator.id"
            class="gp-indicator-item"
          >
            <div class="gp-indicator-info">
              <div class="gp-indicator-visibility" :class="{ hidden: !indicator.v }">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              </div>
              <span class="gp-indicator-name" :class="{ faded: !indicator.v }">
                {{ indicator.name }}
              </span>
            </div>
            
            <div class="gp-indicator-actions">
              <select 
                class="gp-group-select"
                :value="groupName"
                @change="$emit('move-indicator', { indicator, newGroup: $event.target.value })"
              >
                <option v-for="group in allGroups" :key="group" :value="group">
                  {{ group }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="gp-actions">
        <button class="gp-btn gp-btn-danger" @click="handleDelete">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          Delete Group
        </button>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: 'GroupPopover',
  props: {
    visible: { type: Boolean, default: false },
    groupName: { type: String, default: '' },
    items: { type: Array, default: () => [] },
    allGroups: { type: Array, default: () => [] },
    allVisible: { type: Boolean, default: true },
    anchorX: { type: Number, default: 0 },
    anchorY: { type: Number, default: 0 }
  },
  emits: ['close', 'rename', 'delete-group', 'toggle-visibility', 'move-indicator'],
  data() {
    return {
      editName: this.groupName,
      isPositioned: false
    }
  },
  computed: {
    positionStyle() {
      if (!this.isPositioned) return {}
      
      const popoverWidth = 320
      const popoverHeight = 400
      const margin = 8
      
      let left = this.anchorX
      let top = this.anchorY
      
      // Ensure popover stays within viewport
      if (left + popoverWidth > window.innerWidth - margin) {
        left = window.innerWidth - popoverWidth - margin
      }
      if (left < margin) {
        left = margin
      }
      if (top + popoverHeight > window.innerHeight - margin) {
        top = this.anchorY - popoverHeight - margin
      }
      if (top < margin) {
        top = margin
      }
      
      return {
        left: `${left}px`,
        top: `${top}px`
      }
    }
  },
  watch: {
    groupName(newName) {
      this.editName = newName
    },
    visible(isVisible) {
      if (isVisible) {
        this.editName = this.groupName
        this.$nextTick(() => {
          this.isPositioned = true
          if (this.$refs.nameInput) {
            this.$refs.nameInput.focus()
          }
        })
      } else {
        this.isPositioned = false
      }
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleEscape)
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleEscape)
  },
  methods: {
    handleOutsideClick(event) {
      if (this.visible && this.$refs.popover && !this.$refs.popover.contains(event.target)) {
        this.$emit('close')
      }
    },
    handleEscape(event) {
      if (event.key === 'Escape' && this.visible) {
        this.$emit('close')
      }
    },
    handleRename() {
      const newName = this.editName.trim()
      if (newName && newName !== this.groupName) {
        this.$emit('rename', newName)
      }
    },
    handleDelete() {
      this.$emit('delete-group')
    }
  }
}
</script>

<style scoped>
.gp-popover {
  position: fixed;
  z-index: 10001;
  width: 320px;
  max-height: 480px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 13px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Header */
.gp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
  background: #fafbfc;
}

.gp-title-section {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.gp-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gp-indicator-count {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
}

.gp-close-btn {
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.gp-close-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

/* Sections */
.gp-section {
  padding: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.gp-section:last-child {
  border-bottom: none;
}

.gp-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

/* Rename Form */
.gp-rename-form {
  display: flex;
  gap: 8px;
}

.gp-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #1e293b;
  background: #ffffff;
  outline: none;
  transition: all 0.2s ease;
}

.gp-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.gp-input::placeholder {
  color: #9ca3af;
}

/* Section Header */
.gp-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.gp-visibility-toggle {
  display: flex;
  align-items: center;
}

.gp-visibility-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #64748b;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.gp-visibility-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.gp-visibility-btn.active {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.gp-visibility-btn svg:last-child {
  display: none;
}

.gp-visibility-btn.active svg:first-child {
  display: none;
}

.gp-visibility-btn.active svg:last-child {
  display: block;
}

/* Empty State */
.gp-empty-indicators {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: #64748b;
  text-align: center;
}

.gp-empty-indicators svg {
  margin-bottom: 12px;
  opacity: 0.5;
}

.gp-empty-indicators p {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
}

/* Indicators List */
.gp-indicators-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.gp-indicators-list::-webkit-scrollbar {
  width: 4px;
}

.gp-indicators-list::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.gp-indicators-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 2px;
}

.gp-indicator-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.gp-indicator-item:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.gp-indicator-info {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}

.gp-indicator-visibility {
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.gp-indicator-visibility svg:first-child {
  display: block;
}

.gp-indicator-visibility svg:last-child {
  display: none;
  position: absolute;
  top: 0;
  left: 0;
}

.gp-indicator-visibility.hidden svg:first-child {
  display: none;
}

.gp-indicator-visibility.hidden svg:last-child {
  display: block;
}

.gp-indicator-name {
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.gp-indicator-name.faded {
  color: #9ca3af;
}

.gp-indicator-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.gp-group-select {
  font-size: 11px;
  padding: 4px 6px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
}

.gp-group-select:focus {
  border-color: #3b82f6;
}

/* Actions */
.gp-actions {
  padding: 16px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.gp-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.gp-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.gp-btn-primary {
  background: #3b82f6;
  color: #ffffff;
}

.gp-btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.gp-btn-danger {
  background: #ffffff;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.gp-btn-danger:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}
</style>
